import dayjs from 'dayjs';
import { LOGGER_LOG_TYPE, LOGGER_MESSAGE_TYPE, LOGGER_PRINT_LOGS, LOGGER_PRINT_LOGS_LEVEL } from '../../Config';
import LogsService from 'api/logs/LogsService';

class Logger {
    private readonly systemName = '';
    private readonly systemVersion = '';
    private readonly appVersion = '';
    private userId?: number;

    public setUserId (userId: number) {
        this.userId = userId;
    }

    public error (logType: LOGGER_LOG_TYPE, message: string, extraData?: any, allowSend = true): void {
        void this.log(LOGGER_MESSAGE_TYPE.ERROR, logType, message, extraData, allowSend);
    }

    public info (logType: LOGGER_LOG_TYPE, message: string, extraData?: any, allowSend = true): void {
        void this.log(LOGGER_MESSAGE_TYPE.INFO, logType, message, extraData, allowSend);
    }

    private async log (messageType: LOGGER_MESSAGE_TYPE, logType: LOGGER_LOG_TYPE, message: string, extraData?: any, allowSend = true) {
        const data = {
            type: LOGGER_MESSAGE_TYPE[messageType],
            logType: LOGGER_LOG_TYPE[logType],
            date: dayjs.utc(new Date()).format('DD/MM/YYYY hh:mm:ss'),
            systemName: this.systemName,
            systemVersion: this.systemVersion,
            appVersion: this.appVersion,
            userId: this.userId,
            message,
            extraData
        };

        // Console output is gated by the print flag/level (off in production).
        if (LOGGER_PRINT_LOGS && messageType >= LOGGER_PRINT_LOGS_LEVEL) {
            const logMessage = `[${data.date}] ${data.message}: ${data.extraData?.message ?? ''}`;
            if (messageType === LOGGER_MESSAGE_TYPE.ERROR) {
                console.error(logMessage, data);
            } else {
                console.info(logMessage, data);
            }
        }

        // Errors are always forwarded to the backend (which relays them to the external logging API),
        // regardless of the console print flag. `allowSend` is the anti-recursion guard used when the
        // failing request is itself the call to the /logger endpoint.
        if (messageType === LOGGER_MESSAGE_TYPE.ERROR && allowSend) {
            await LogsService.log(this.buildPayload(data));
        }
    }

    private buildPayload (data: any) {
        const err = data.extraData?.error ?? data.extraData?.reason ?? data.extraData;
        const stackTrace = err?.stack ?? data.extraData?.stack;

        return {
            message: data.message,
            stackTrace,
            level: data.type,
            url: typeof window !== 'undefined' ? window.location.href : undefined,
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
            additionalData: this.safeStringify({
                logType: data.logType,
                userId: data.userId,
                date: data.date,
                data: data.extraData
            })
        };
    }

    private safeStringify (value: any): string {
        try {
            return JSON.stringify(value, (_key, val) =>
                val instanceof Error ? { name: val.name, message: val.message, stack: val.stack } : val
            );
        } catch {
            return String(value);
        }
    }
}

export default new Logger();
